import { wantsHelp } from './helpers.js'
import { isJsonMode, jsonSuccess, muteForJson } from '../output.js'
import { doctorHeadline, doctorSummary } from '../../core/doctor/format.js'

/**
 * `openacp doctor` — Check OpenACP health and optionally fix issues.
 *
 * In dry-run mode (or with --json), checks are reported but no fixes are applied.
 * In interactive mode, fixable issues prompt the user before applying.
 * Exits with code 1 if any check fails.
 */
export async function cmdDoctor(args: string[], instanceRoot?: string): Promise<void> {
  const json = isJsonMode(args)
  if (json) await muteForJson()

  if (!json && wantsHelp(args)) {
    console.log(`
\x1b[1mopenacp doctor\x1b[0m — Check OpenACP health

\x1b[1mUsage:\x1b[0m
  openacp doctor [--dry-run]

\x1b[1mOptions:\x1b[0m
  --dry-run       Check only, don't apply any fixes
  --json          Output result as JSON
  -h, --help      Show this help message

Checks your OpenACP installation for common issues including
config validity, agent availability, dependencies, and connectivity.
Fixable issues can be auto-repaired when not using --dry-run.
`)
    return
  }

  const knownFlags = ["--dry-run", "--json"];
  const unknownFlags = args.filter(
    (a) => a.startsWith("--") && !knownFlags.includes(a),
  );
  if (!json && unknownFlags.length > 0) {
    const { suggestMatch } = await import('../suggest.js');
    for (const flag of unknownFlags) {
      const suggestion = suggestMatch(flag, knownFlags);
      console.error(`Unknown flag: ${flag}`);
      if (suggestion) console.error(`Did you mean: ${suggestion}?`);
    }
    process.exit(1);
  }

  // --json implies --dry-run
  const dryRun = args.includes("--dry-run") || json;
  const { DoctorEngine } = await import("../../core/doctor/index.js");
  const engine = new DoctorEngine({ dryRun, dataDir: instanceRoot });

  if (!json) console.log("\n🩺 OpenACP Doctor\n");

  const report = await engine.runAll();

  if (json) {
    jsonSuccess({
      categories: report.categories.map((c) => ({
        name: c.name,
        results: c.results.map((r) => ({ status: r.status, message: r.message })),
      })),
      summary: {
        passed: report.summary.passed,
        warnings: report.summary.warnings,
        failed: report.summary.failed,
      },
    })
  }

  // Render issues first so the next action is visible without scanning passes.
  const icons = { pass: "\x1b[32m✅\x1b[0m", warn: "\x1b[33m⚠️\x1b[0m", fail: "\x1b[31m❌\x1b[0m" };
  const { passed, failed } = report.summary;
  console.log(`\x1b[1m${doctorHeadline(report.summary)}\x1b[0m — ${doctorSummary(report.summary)}\n`);

  for (const category of report.categories.filter((item) => item.results.some((result) => result.status !== 'pass'))) {
    console.log(`\x1b[1m\x1b[36m${category.name}\x1b[0m`);
    for (const result of category.results.filter((item) => item.status !== 'pass')) {
      console.log(`  ${icons[result.status]} ${result.message}`);
    }
    console.log();
  }

  // Handle risky fixes
  if (report.pendingFixes.length > 0) {
    console.log("\x1b[1mFixable issues:\x1b[0m\n");
    for (const pending of report.pendingFixes) {
      if (dryRun) {
        console.log(`  🔧 ${pending.message} (use without --dry-run to fix)`);
      } else {
        const clack = await import("@clack/prompts");
        const shouldFix = await clack.confirm({
          message: `Fix: ${pending.message}?`,
          initialValue: false,
        });
        if (clack.isCancel(shouldFix) || !shouldFix) {
          continue;
        }
        const fixResult = await pending.fix();
        if (fixResult.success) {
          console.log(`  \x1b[32m✓ ${fixResult.message}\x1b[0m`);
        } else {
          console.log(`  \x1b[31m✗ Fix failed: ${fixResult.message}\x1b[0m`);
        }
      }
    }
    console.log();
  }

  console.log(`${passed} passed`);

  if (failed > 0) {
    process.exit(1);
  }
}
